import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import { getWeekStreak } from '../../services/historyService';
import { colors as theme } from '../../theme';

const colors = {
  bg: theme.bg,
  surface1: theme.bg,
  surface2: theme.surface1,
  border: 'rgba(255, 255, 255, 0.08)',
  text: theme.text,
  textMuted: theme.textMuted,
  accent: theme.gold,
  success: theme.success,
};

const typography = {
  h3: { fontSize: '18px', fontWeight: '600', lineHeight: '1.4' },
  body: { fontSize: '15px', fontWeight: '400', lineHeight: '1.6' },
  small: { fontSize: '13px', fontWeight: '500', lineHeight: '1.5' },
  tiny: { fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' },
};

/**
 * Workout Heatmap
 * Calendar view showing training days as green squares
 * Shows patterns, streaks, and consistency
 */
const WorkoutHeatmap = ({ userId, programId }) => {
  const [heatmapData, setHeatmapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalDays: 0, currentStreak: 0, longestStreak: 0 });

  useEffect(() => {
    if (userId) {
      fetchHeatmapData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchHeatmapData = async () => {
    try {
      setLoading(true);

      // Anchor start to a Sunday so each row always lines up with the correct day label.
      // Calculate up-front so we can use it as a query filter (avoids the Supabase
      // default 1,000-row cap silently dropping logs and creating phantom gaps).
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayDow = today.getDay(); // 0 = Sunday
      // Go back to the Sunday that starts 12 weeks ago
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - todayDow - 77); // 11 full weeks back + current week start

      // Get every completed log within the visible 12-week window
      const { data: logs, error: logsError } = await supabase
        .from('workout_logs')
        .select('created_at')
        .eq('user_id', userId)
        .eq('completed', true)
        .gte('created_at', startDate.toISOString())
        .limit(10000);

      if (logsError) throw logsError;

      if (!logs || logs.length === 0) {
        setHeatmapData([]);
        setLoading(false);
        return;
      }

      // Convert each log timestamp to a local-date string (browser timezone)
      // so a 10pm Sunday session isn't bumped to Monday by UTC conversion
      const workoutDates = logs.map(l => {
        const d = new Date(l.created_at);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      });

      const uniqueDates = [...new Set(workoutDates)];
      // Weeks in a row, the one streak every screen shows (task 018, R1); the calendar itself stays by day
      const { current, longest } = await getWeekStreak(userId);

      setStats({
        totalDays: uniqueDates.length,
        currentStreak: current,
        longestStreak: longest,
      });

      const calendarData = [];
      const dateSet = new Set(uniqueDates);

      for (let i = 0; i < 84; i++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + i);
        const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
        const isFuture = currentDate > today;

        calendarData.push({
          date: dateStr,
          hasWorkout: !isFuture && dateSet.has(dateStr),
          dayOfWeek: currentDate.getDay(),
          weekIndex: Math.floor(i / 7),
          isFuture,
        });
      }

      setHeatmapData(calendarData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching heatmap data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h3 style={{ ...typography.h3, color: colors.text, margin: '0 0 16px 0' }}>
          Training Calendar
        </h3>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            width: '32px', height: '32px', margin: '0 auto 12px',
            border: '3px solid rgba(212, 175, 55, 0.2)',
            borderTop: `3px solid ${theme.gold}`,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <div style={{ color: theme.textMuted, fontSize: '13px' }}>Loading...</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (heatmapData.length === 0) {
    return (
      <div style={styles.container}>
        <h3 style={{ ...typography.h3, color: colors.text, margin: '0 0 16px 0' }}>
          Training Calendar
        </h3>
        <div style={{ textAlign: 'center', padding: '40px 20px', color: theme.textMuted }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>📊</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: theme.text, marginBottom: '4px' }}>
            No data yet
          </div>
          <div style={{ fontSize: '13px' }}>Complete workouts to see your training calendar here.</div>
        </div>
      </div>
    );
  }

  const weeks = 12;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={{ ...typography.h3, color: colors.text, margin: 0 }}>
          Training Calendar
        </h3>
        <span style={{ ...typography.tiny, color: colors.textMuted }}>
          LAST 12 WEEKS
        </span>
      </div>

      {/* Heatmap Grid */}
      <div style={styles.heatmapContainer}>
        {/* Day labels */}
        <div style={styles.dayLabels}>
          <span style={{ ...typography.tiny, color: colors.textMuted, fontSize: '11px', fontWeight: '500' }}>S</span>
          <span style={{ ...typography.tiny, color: colors.textMuted, fontSize: '11px', fontWeight: '500' }}>M</span>
          <span style={{ ...typography.tiny, color: colors.textMuted, fontSize: '11px', fontWeight: '500' }}>T</span>
          <span style={{ ...typography.tiny, color: colors.textMuted, fontSize: '11px', fontWeight: '500' }}>W</span>
          <span style={{ ...typography.tiny, color: colors.textMuted, fontSize: '11px', fontWeight: '500' }}>T</span>
          <span style={{ ...typography.tiny, color: colors.textMuted, fontSize: '11px', fontWeight: '500' }}>F</span>
          <span style={{ ...typography.tiny, color: colors.textMuted, fontSize: '11px', fontWeight: '500' }}>S</span>
        </div>

        {/* Calendar grid */}
        <div style={styles.grid}>
          {Array.from({ length: weeks }).map((_, weekIndex) => (
            <div key={weekIndex} style={styles.week}>
              {Array.from({ length: 7 }).map((_, dayIndex) => {
                const dataIndex = weekIndex * 7 + dayIndex;
                const dayData = heatmapData[dataIndex];

                if (!dayData) return null;

                const date = new Date(dayData.date);
                const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                const formattedDate = date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                });

                return (
                  <div
                    key={dayIndex}
                    style={{
                      ...styles.cell,
                      backgroundColor: dayData.hasWorkout ? colors.success : colors.surface2,
                      border: dayData.hasWorkout ? 'none' : `1px solid ${colors.border}`,
                      opacity: dayData.isFuture ? 0.25 : 1,
                      cursor: 'default',
                    }}
                    title={`${dayName}, ${formattedDate}${dayData.hasWorkout ? ' - Workout completed' : ''}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        <div style={styles.statItem}>
          <span style={{ ...typography.tiny, color: colors.textMuted }}>TRAINING DAYS</span>
          <span style={{ ...typography.h3, color: colors.accent, fontSize: '24px' }}>{stats.totalDays}</span>
        </div>
        <div style={styles.statItem}>
          <span style={{ ...typography.tiny, color: colors.textMuted }}>WEEKS IN A ROW</span>
          <span style={{ ...typography.h3, color: colors.success, fontSize: '24px' }}>{stats.currentStreak}</span>
        </div>
        <div style={styles.statItem}>
          <span style={{ ...typography.tiny, color: colors.textMuted }}>BEST RUN (WEEKS)</span>
          <span style={{ ...typography.h3, color: colors.accent, fontSize: '24px' }}>{stats.longestStreak}</span>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    backgroundColor: colors.surface1,
    borderRadius: '14px',
    border: `1px solid ${colors.border}`,
  },
  header: {
    marginBottom: '20px',
  },
  heatmapContainer: {
    display: 'flex',
    gap: '16px',
    marginBottom: '20px',
  },
  dayLabels: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    gap: '4px',
  },
  grid: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'nowrap',
    overflowX: 'auto',
  },
  week: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  cell: {
    width: '16px',
    height: '16px',
    borderRadius: '4px',
    flexShrink: 0,
    transition: 'all 0.2s ease',
  },
  stats: {
    marginTop: '24px',
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '12px 16px',
    backgroundColor: colors.surface2,
    borderRadius: '14px',
    border: `1px solid ${colors.border}`,
    minWidth: '100px',
  },
};

export default WorkoutHeatmap;
